import React, { FC, Fragment, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from 'config/constants';
import { useMutation } from '@apollo/client/react';
import { fortnoxTokenLoginTemplate } from 'apollo/templates/fortnox';

const FortnoxAuthLandingPage: FC = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const [authState, setAuthState] = useState(AuthState.AUTH_IN_PROGRESS);
    const { t: translate } = useTranslation(['invoices']);

    const [fortnoxTokenLogin] = useMutation(fortnoxTokenLoginTemplate, {
        onCompleted: ({ fortnoxTokenLogin }) => {
            if (fortnoxTokenLogin) {
                setAuthState(AuthState.AUTH_SUCCESSFUL);
                setTimeout(() => {
                    navigate('/orders/list');
                }, 3000);
                return;
            }
            setAuthState(AuthState.AUTH_FAILD);
        },
        onError: () => {
            setAuthState(AuthState.AUTH_FAILD);
        },
    });

    useEffect(() => {
        const authCode = new URLSearchParams(search).get('code');
        const returnState = new URLSearchParams(search).get('state');
        const compareState = localStorage.getItem(CONSTANTS.FORTNOX_TEMP_STATE_KEY);

        if (authCode && returnState === compareState) {
            fortnoxTokenLogin({ variables: { authCode: authCode } });
            return;
        }
        console.error('Fortnox auth failed: Invalid state or missing auth code');
        setAuthState(AuthState.AUTH_FAILD);
    }, [search]);

    return (
        <Fragment>
            <Box display="flex" justifyContent="center" sx={{ minHeight: 300 }}>
                <Box sx={{ margin: 'auto', maxWidth: 600 }}>
                    {(() => {
                        switch (authState) {
                            case AuthState.AUTH_IN_PROGRESS:
                                return (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <CircularProgress variant="indeterminate" color="secondary" />
                                        <Typography variant="h3" mt={2}>
                                            {translate('invoices:PROCESSING_FORTNOX_LOGIN')}
                                        </Typography>
                                    </Box>
                                );
                            case AuthState.AUTH_SUCCESSFUL:
                                return (
                                    <Alert severity="success">
                                        <AlertTitle>{translate('invoices:FORTNOX_AUTH_SUCCESSFUL_TITLE')}</AlertTitle>
                                        <Box>{translate('invoices:FORTNOX_AUTH_SUCCESSFUL_BODY')}</Box>
                                    </Alert>
                                );
                            case AuthState.AUTH_FAILD:
                                return (
                                    <Alert severity="error">
                                        <AlertTitle>{translate('invoices:FORTNOX_AUTH_ERROR_TITLE')}</AlertTitle>
                                        <Box>{translate('invoices:FORTNOX_AUTH_ERROR_BODY')}</Box>
                                    </Alert>
                                );
                            default:
                                return null;
                        }
                    })()}
                </Box>
            </Box>
        </Fragment>
    );
};

enum AuthState {
    AUTH_IN_PROGRESS,
    AUTH_SUCCESSFUL,
    AUTH_FAILD,
}

export default FortnoxAuthLandingPage;
